import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthResponse, LoginCredentials } from '../types';
import { authService } from '../services/api';
import { useOAuth } from './useOAuth';
import toast from 'react-hot-toast';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('authToken'));
  const [isLoading, setIsLoading] = useState(true);
  // The OAuth provider owns the in-memory access token; reaching through its
  // context is the only way to invalidate the refresh cookie at signout.
  const { logout: oauthLogout } = useOAuth();

  const isAuthenticated = !!user && !!token;

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('authToken');
      if (savedToken) {
        try {
          const currentUser = await authService.getCurrentUser();
          setUser(currentUser);
          setToken(savedToken);
          // Ensure currentUserId is in sync
          if (currentUser.id) {
            localStorage.setItem('currentUserId', currentUser.id);
          }
        } catch (error) {
          // Token is invalid, clear it
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('currentUserId');
          setToken(null);
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = async (credentials: LoginCredentials): Promise<boolean> => {
    try {
      setIsLoading(true);
      
      // Check if a different user is already logged in
      const currentUserId = localStorage.getItem('currentUserId');
      if (currentUserId) {
        // Clear the previous user's session
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('currentUserId');
        setUser(null);
        setToken(null);
        
        // Notify user about the logout
        toast('Previous session has been logged out', {
          duration: 3000,
          icon: 'ℹ️',
        });
      }
      
      const authResponse: AuthResponse = await authService.login(credentials);
      
      setUser(authResponse.user);
      setToken(authResponse.tokens.accessToken);
      localStorage.setItem('authToken', authResponse.tokens.accessToken);
      localStorage.setItem('refreshToken', authResponse.tokens.refreshToken);
      localStorage.setItem('currentUserId', authResponse.user.id);
      
      toast.success('Login successful!');
      return true;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Login failed. Please try again.';
      toast.error(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('currentUserId');

    // Invalidate the WayFong session (access token + refresh cookie) via the
    // OAuth context, which holds the current in-memory access token.
    oauthLogout().catch(() => {
      // Ignore errors — local state has already been cleared.
    });

    // Call logout API in the background
    authService.logout().catch(() => {
      // Ignore errors on logout API call
    });

    toast.success('Logged out successfully');
  };

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    login,
    logout,
    isAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};